import GiftCard, { GiftCardState, GiftCardProps } from "./GiftCard";
import { Product, Sku, StoredCreditCard, CreditCard, ReceiptTrx, RecipientInformation, UserBrowserInfo, Country } from "../types";
import { isMobileNumberValid, scrollWindow } from "../utils";
import { CatalogService } from "../services/catalog-service";
import { CreditCardFactory } from '../common/credit-card-factory';
import { FingerprintFactory } from '../common/finger-print-factory';
import { TopupService } from "../services/topup-service";
import { dialogService } from "react-api-dialog";
import t from "../i18n";
import { messageDialog } from "../dialogs/MessageDialog";
import ThreeDSecure from "../common/3DSeccure";
import { GTMDataLayer } from "../services/gtm-datalayer";
import { confirmDialog } from "../dialogs/ConfirmDialog";
import { SkuCategory } from "../types-enums";
import { fetchGuestEmail, showLogin } from "../services/helper-service";
import { AuthService } from "../auth/auth-service";

export default class GiftCardManager {
    constructor(private instance: GiftCard) {
    }
    get state() {
        return this.instance.state;
    }
    setState<K extends keyof GiftCardState>(state: ((prevState: Readonly<GiftCardState>, props: Readonly<GiftCardProps>) =>
        (Pick<GiftCardState, K> | GiftCardState | null)) | (Pick<GiftCardState, K> | GiftCardState | null),
        callback?: () => void): void {
        // @ts-ignore
        this.instance.setState(state, callback);
    }
    onCountryOrMobileNumberChanged = async (country: Country, mobileNumber: string) => {
        let products = this.state.products;
        const selectedCountry = this.state.selectedCountry;
        let selectedProduct: Product | undefined = this.state.siteSettings.isSingleOperator ? this.state.selectedProduct : undefined;
        const isMobileValid = isMobileNumberValid(country, selectedProduct, mobileNumber);
        if (!selectedCountry || selectedCountry.countryCode !== country.countryCode) {
            await this.toggleLoader(true);
            //const productsObj = await CatalogService.getProducts(country.countryCode, this.state.session.sourceCurrencyCode);
            const productsObj = await CatalogService.getGiftCardProducts(country.countryCode, this.state.session.sourceCurrencyCode);
            products = productsObj.products;
            if (this.state.siteSettings.isSingleOperator) {
                selectedProduct = products[0];
            }
            await this.toggleLoader(false);
        }
        const step = this.state.step.set(isMobileValid && selectedProduct ? "order" : isMobileValid ? "operator" : "number");
        this.setState({
            selectedCountry: country, session: {
                ...this.state.session,
                currencyCode: country.currencyCode,
                countryCode: country.countryCode, internationalCode: country.internationalCode,
                mobileNumber: mobileNumber, isValidMobileNumber: isMobileValid,
                productId: selectedProduct ? selectedProduct.productId : undefined,
                skuId: undefined
            }, products: products, selectedProduct: selectedProduct, step: step
        });
        return step;
    }
    onProductChanged = async (product: Product) => {
        const tnc = await CatalogService.getProductTermsAndConditions(product.productId);
        this.setState({
            selectedProduct: { ...product, productDescription: tnc.giftCardDescription },
            session: {
                ...this.state.session,
                productId: product.productId,
                productCategory: product.productCategory
            },
            step: this.state.step.set("order")
        }, () => {
            scrollWindow(document.body.scrollHeight, 1000);
        });
    }
    onSkuChanged = (sku: Sku | undefined) => {
        this.setState({
            session: {
                ...this.state.session,
                skuId: sku ? sku.skuId : undefined,
                faceValue: sku ? sku.faceValue : 0,
                localCurrencyAmount: sku ? sku.localCurrencyAmount : undefined,
                salesTax: this.state.selectedProduct!.salesTax || 0,
                coverSalesTax: sku && sku.category === SkuCategory.Data ? false : this.state.session.coverSalesTax,
                productCategory: this.state.selectedProduct!.productCategory,
                // reset coverSalesTax flag when SKU is changed to handle Data SKUs 
            }
        }, () => {
            scrollWindow(document.body.scrollHeight, 500);
        });
    }
    applyCouponCode = async (couponCode: string) => {
        const tempSession = { ...this.state.session, promoCode: couponCode };
        await this.toggleLoader(true);
        const response = await TopupService.saveSession(tempSession);
        await this.toggleLoader(false);
        if (!response) return;
        if (response.responseCode === 0) {
            this.setState({ session: response.payLoad.session, siteSettings: { ...response.payLoad.siteSettings, showSMS: false } });
            //Toaster.show(strings('validations.coupon_code_applied'));
        } else {
            Toaster.show(response.responseMessage);
        }
    }
getStorePinBalance = async (couponCode: string) => {
        debugger;
        const tempSession = { ...this.state.session, promoCode: couponCode };
        await this.toggleLoader(true);
        const response = await TopupService.getStorePinBalance(tempSession);
        debugger;
        await this.toggleLoader(false);
        if (!response) return;
        if (response.responseCode === 0) {
            this.setState({ session: response.payLoad.session, siteSettings: response.payLoad.siteSettings });
        } else {
            Toaster.show(response.responseMessage);
        }
    }    
    applyRewardPoints = async (rewardPoints: string) => {
        if (+rewardPoints > (this.state.session.maxDiscountValue || 0)) {
            Toaster.show(t.error("reward_points_limit_error_message", {
                discount: this.state.session.maxDiscountValue,
                currency: this.state.session.sourceCurrencyCode
            }));
            return;
        } else if (+rewardPoints < (this.state.session.minDiscountValue || 0)) {
            Toaster.show(t.error("reward_points_min_limit_error_message", {
                discount: this.state.session.minDiscountValue,
                currency: this.state.session.sourceCurrencyCode
            }));
            return;
        } else if (isNaN(+rewardPoints)) {
            Toaster.show(t.error("reward_points_invalid_number"));
            return;
        }
        const tempSession = { ...this.state.session, rewardPointsUsed: Math.round(+rewardPoints * 100) };
        await this.toggleLoader(true);
        const response = await TopupService.saveSession(tempSession);
        await this.toggleLoader(false);
        if (!response) return;
        if (response.responseCode === 0) {
            this.setState({ session: response.payLoad.session, siteSettings: { ...response.payLoad.siteSettings, showSMS: false } });
            // Toaster.show(strings('validations.reward_points_applied_success'));
        } else {
            Toaster.show(response.responseMessage);
        }
    }
    paymentMethodChanged = (paymentMethod: "cc" | "pp") => {
        this.setState({
            payment: { ...this.state.payment, paymentMethod: paymentMethod }
        });
    }
    creditCardMethodChanged = (creditCardMethod: "new" | "stored") => {
        this.setState({
            payment:
            {
                paymentMethod: this.state.payment.paymentMethod,
                creditCardPayment: { ...this.state.payment.creditCardPayment, creditCardMethod: creditCardMethod }
            }
        });
    }
    creditCardChanged = (newCard: CreditCard) => {
        this.setState({
            payment: {
                ...this.state.payment,
                creditCardPayment: {
                    ...this.state.payment.creditCardPayment,
                    creditCard: newCard
                }
            }
        });
    }
    recipientInfoChanged = (info: RecipientInformation) => {
        this.setState({ recipientInfo: info });
    }
    storedCreditCardChanged = (storedCard: StoredCreditCard) => {
        this.setState({
            payment: {
                ...this.state.payment,
                creditCardPayment: {
                    ...this.state.payment.creditCardPayment,
                    storedCard: storedCard
                }
            }
        });
    }
    navigateToSummary = async () => {
        // if not guest show login popup
        let guestEmail = fetchGuestEmail();
        if (!guestEmail && this.state.siteSettings.enableGuestCheckout) {
            const isLoggedIn = await AuthService.isLoggedIn()
            if (!isLoggedIn) {
                const resp = await showLogin();
                if (resp.cancelled) return;
            }
        }
        // once login popup closes, check if the user log-in or checkout as guest
        guestEmail = fetchGuestEmail();
        if (!guestEmail) {
            dialogService.showPreloader();
            const homeCurrencyRespnose = await TopupService.fetchHomeCurrency();
            if (!homeCurrencyRespnose) {
                await dialogService.hidePreloader();
                return false;
            }
            if (homeCurrencyRespnose.responseCode !== 0) {
                await dialogService.hidePreloader();
                messageDialog(`${homeCurrencyRespnose.responseMessage}`, t.common("oops"))
                return false;
            }
            const homeCurrency = homeCurrencyRespnose.payLoad;
            const sourceCurrencyCode = this.state.session.sourceCurrencyCode;
            if (homeCurrency && this.state.siteSettings.supportedCurrencies.indexOf(homeCurrency) > -1 && (homeCurrency !== sourceCurrencyCode)) {
                await dialogService.hidePreloader();
                await dialogService.hidePreloader();
                await dialogService.hidePreloader();  // Hack: sometimes the preloader does not go away, so calling the hide() again. Need to investigate in detail
                // await messageDialog(`Please choose the amount in ${homeCurrency}`, "Top-up"); //, `Pay in ${sourceCurrencyCode}`, `Pay in ${homeCurrency}`);
                // return "NEW_TOPUP";
                const value = await confirmDialog(t.messages("different_currency", { homeCurrency: homeCurrency, trxCurrency: sourceCurrencyCode }),
                    t.common("top-up"),
                    t.messages("pay_in_curr", { currency: sourceCurrencyCode }),
                    t.messages("pay_in_curr", { currency: homeCurrency }));
                if (value.cancelled) {
                    return "NEW_TOPUP";
                } else {
                    dialogService.showPreloader();
                }
            }
        }
        else {
            // if guest checkout then assign the guestEmail in session
            this.setState({ session: { ...this.state.session, guestEmail: guestEmail || undefined } });
        }
        // const isMobileValid = isMobileNumberValid(this.state.selectedCountry, this.state.selectedProduct, this.state.session.mobileNumber);
        // if (!isMobileValid) {
        //     await dialogService.hidePreloader();
        //     await messageDialog(t.validations("mobile_number_invalid"), t.common("top-up"));
        //     return "MOBILE_INVALID";
        // }
        this.setState({
            session: {
                ...this.state.session,
                mobileNumber: this.state.recipientInfo.recipientMobile,
                recipientMobile: this.state.recipientInfo.recipientMobile,
                senderMobile: this.state.recipientInfo.senderMobile,
                recipientEmail: this.state.recipientInfo.recipientEmail,
                recipientFullName: this.state.recipientInfo.recipientFullName,
                recipientMessage: this.state.recipientInfo.recipientMessage,
                senderFullName: this.state.recipientInfo.senderFullName,
            }
        });
        const response = await TopupService.saveSession(this.state.session);
        if (!response) {
            await dialogService.hidePreloader();
            return false;
        }
        if (response.responseCode !== 0) {
            await dialogService.hidePreloader();
            messageDialog(`${response.responseMessage}`, t.common("oops"))
            return false;
        }
        const payLoad = response.payLoad;
        payLoad.session.isValidMobileNumber = true;
        this.setState({ session: payLoad.session, siteSettings: { ...payLoad.siteSettings, showSMS: false }, products: payLoad.products, countries: payLoad.countries, step: this.state.step.set("summary") })
        GTMDataLayer.addToCart(this.state.selectedProduct, payLoad.session);
        dialogService.hidePreloader();
        return "SUMMARY";
    }
    navigateToPayment = async () => {
        dialogService.showPreloader();
        const session = this.state.session;
        session.reachedPaymentPage = true;
        const guestEmail = fetchGuestEmail();
        if (!guestEmail) {
            // if the user has logged in and guest email is NULL
            this.setState({ session: { ...this.state.session, guestEmail: undefined } });
        }
        const response = await TopupService.saveSession(this.state.session);
        if (!response) {
            await dialogService.hidePreloader();
            return false;
        };
        if (response.responseCode !== 0) {
            await dialogService.hidePreloader();
            messageDialog(`${response.responseMessage}`, t.common("oops"))
            return false;
        }
        const payLoad = response.payLoad;
        payLoad.session.isValidMobileNumber = true;
        const settings = await this.loadCreditCardDetails(session.sourceCurrencyCode);
        const isSuccessful = !!settings;
        dialogService.hidePreloader();
        const storedCreditCards = settings ? settings.storedCreditCards : [];
        this.setState({
            storedCreditCards: storedCreditCards,
            paymentCountries: settings ? settings.paymentCountries : [],
            addressSettings: settings ? settings.addressSettings : undefined,
            creditCardHelper: settings ? settings.creditCardHelper : undefined,
            session: payLoad.session,
            siteSettings: { ...payLoad.siteSettings, showSMS: false },
            products: payLoad.products,
            countries: payLoad.countries,
            step: this.state.step.set(isSuccessful ? "payment" : "summary"),
            payment: {
                ...this.state.payment,
                paymentMethod: response.payLoad.session.netCharge === 0 ? "zz" : this.state.payment.paymentMethod,
                creditCardPayment: {
                    ...this.state.payment.creditCardPayment,
                    creditCardMethod: storedCreditCards.length === 0 ? "new" : "stored",
                    storedCard: storedCreditCards.length > 0 ? storedCreditCards[0] : undefined
                }
            }
        });
        GTMDataLayer.checkout(this.state.selectedProduct, payLoad.session);
        return isSuccessful;
    }
    processNewCreditCard = async (sid: string, ccInfo: CreditCard | undefined, browserInfo: UserBrowserInfo) => {
        if (!this.state.creditCardHelper) return undefined;
        if (!ccInfo) return undefined;
        await this.toggleLoader(true);
        const creditCardNumber = ccInfo.creditCardNumber.replace(/ /g, "");
        const token = await this.state.creditCardHelper.tokenize(ccInfo);
        const fingerPrintSessions = FingerprintFactory.getWebSessionId(this.state.fingerPrintHelpers);
        const response = await TopupService.postCCTransaction(sid, { ...ccInfo, creditCardNumber, creditCardToken: token }, fingerPrintSessions, browserInfo);
        await this.toggleLoader(false);
        return response;
    }
    processStoredCreditCard = async (sid: string, creditCardId: number, cvvCode: string, browserInfo: UserBrowserInfo) => {
        if (!this.state.creditCardHelper) return undefined;
        const storedCard = this.state.payment.creditCardPayment.storedCard;
        if (!storedCard) return undefined;
        await this.toggleLoader(true);
        const fingerPrintSessions = FingerprintFactory.getWebSessionId(this.state.fingerPrintHelpers);
        const response = await TopupService.postStoredCCTransaction(sid, cvvCode, creditCardId, fingerPrintSessions, browserInfo);
        await this.toggleLoader(false);
        return response;
    }
    processCreditCard = async () => {
        let response: { responseCode: number, responseMessage: string, payLoad: ReceiptTrx } | undefined;
        var date = new Date();
        var browserInfo: UserBrowserInfo = { screenHeight: screen.height || undefined, screenColorDepth: screen.colorDepth || undefined, screenWidth: screen.width || undefined, timezone: date.getTimezoneOffset() || undefined, language: navigator.language || "", isJavaEnabled: navigator.javaEnabled() }
        if (this.state.payment.creditCardPayment.creditCardMethod === 'new') {
            response = await this.processNewCreditCard(this.state.session.sessionId, this.state.payment.creditCardPayment.creditCard, browserInfo);
        } else {
            const card = this.state.payment.creditCardPayment.storedCard;
            if (!card) return undefined;
            response = await this.processStoredCreditCard(this.state.session.sessionId, card.creditCardId, card.cvvCode!, browserInfo);
        }
        if (!response) return;
        if (response.responseCode !== 0) {
            messageDialog(response.responseMessage, t.common("oops"));
            return undefined;
        }
        const payLoad = response.payLoad!;
        if (!payLoad.redirectUrl) {
            if (payLoad.receipt.rechargeStatus === 1) {
                GTMDataLayer.transactionComplete(payLoad.receipt);
            }
            if (response.payLoad!.receipt.guestTransactionId) {
                return `/receipt?guestTrxId=${response.payLoad!.receipt.guestTransactionId}`;
            }
            else {
                return `/receipt?transactionId=${response.payLoad!.receipt.transactionId}`;
            }
        } else {
            const trxId = response.payLoad!.trxId!;
            const result = await dialogService.showComponent<{ payLoad: boolean }>(ThreeDSecure, { hash: "3d", animation: "slide-down", style: { bottom: 0 } }, {
                url: response.payLoad!.redirectUrl,
                transactionId: trxId
            });
            if (result.cancelled || !result.payLoad) return;
            await this.toggleLoader(true);
            const threeDResponse = await TopupService.proces3DTransaction(trxId);
            await this.toggleLoader(false);
            if (!threeDResponse) return;
            if (threeDResponse.responseCode === 0) {
                GTMDataLayer.transactionComplete(threeDResponse.payLoad!.receipt);
                if (threeDResponse.payLoad!.receipt.guestTransactionId) {
                    return `/receipt?guestTrxId=${threeDResponse.payLoad!.receipt.guestTransactionId}`;
                }
                else {
                    return `/receipt?transactionId=${threeDResponse.payLoad!.receipt.transactionId}`;
                }
            } else {
                await messageDialog(threeDResponse.responseMessage, t.common("oops"));
                return undefined;
            }
        }
    }
    processPaypal = async () => {
        await this.toggleLoader(true);
        //TODO: Remove hardcoding
        const successUrl = `${location.origin}${location.pathname}#/paypal?sid=${this.state.session.sessionId}`;
        const cancelUrl = `${location.origin}${location.pathname}#/topup?sid=${this.state.session.sessionId}&step=payment`;
        const response = await TopupService.initiatePaypalTransaction(this.state.session.sessionId, successUrl, cancelUrl);
        if (!response) {
            await this.toggleLoader(false);
            Toaster.show('constants.auth_failed');
            return;
        }
        if (response.responseCode === 0) {
            const payLoad = response.payLoad;
            if (payLoad) {
                window.location.href = payLoad.redirectUrl;
            } else {
                await this.toggleLoader(false);
                Toaster.show(response.responseMessage);
            }
        } else {
            await this.toggleLoader(false);
            Toaster.show(response.responseMessage);
        }
        return undefined;
    }
    processZeroPayment = async () => {
        await this.toggleLoader(true);
        const response = await TopupService.postZZTransaction(this.state.session.sessionId);
        await this.toggleLoader(false);
        if (!response) {
            return undefined;
        }
        if (response.responseCode === 0) {
            if (response.payLoad!.receipt.guestTransactionId) {
                return `/receipt?guestTrxId=${response.payLoad!.receipt.guestTransactionId}`;
            }
            else {
                return `/receipt?transactionId=${response.payLoad!.receipt.transactionId}`;
            }
        } else {
            messageDialog(response.responseMessage, t.common("oops"));
            return undefined;
        }
    }
    toggleLoader = async (show: boolean) => {
        if (show !== true) show = false;
        if (show) dialogService.showPreloader();
        if (!show) await dialogService.hidePreloader();
    }
    loadCreditCardDetails = async (sourceCurrencyCode: string) => {
        const response = await CatalogService.fetchCardsAndCounties(this.state.session.sessionId, sourceCurrencyCode);
        if (!response || response.responseCode !== 0) return undefined;
        const { cards: storedCreditCards, countries, addressSettings, creditCardSettings, siteSettings } = response.payLoad;
        const creditCardHelper = CreditCardFactory.getHelper(creditCardSettings);
        return {
            storedCreditCards: storedCreditCards,
            paymentCountries: countries,
            addressSettings: addressSettings,
            siteSettings: { ...siteSettings, showSMS: false },
            creditCardHelper
        };
    }
}
const Toaster = {
    show: (message: string) => {
        messageDialog(message, 'oops');
    }
}
